import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { fetchEvents } from "@/lib/eventsApi";
import type { EventListQuery, SortOrder } from "@cdt/shared";

export type PriorityReviewPreset = "this_week" | "next_week" | "all";

function getDateRange(preset: PriorityReviewPreset): {
  dateFrom?: string;
  dateTo?: string;
} {
  const now = new Date();
  const toIso = (d: Date) => d.toISOString().slice(0, 10);

  if (preset === "all") return {};

  // Monday of the current week (ISO: Monday=1)
  const dayOfWeek = now.getDay() === 0 ? 7 : now.getDay(); // Sun=7
  const monday = new Date(now);
  monday.setDate(now.getDate() - dayOfWeek + 1);
  monday.setHours(0, 0, 0, 0);

  if (preset === "this_week") {
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { dateFrom: toIso(monday), dateTo: toIso(sunday) };
  }

  // next_week
  const nextMonday = new Date(monday);
  nextMonday.setDate(monday.getDate() + 7);
  const nextSunday = new Date(nextMonday);
  nextSunday.setDate(nextMonday.getDate() + 6);
  return { dateFrom: toIso(nextMonday), dateTo: toIso(nextSunday) };
}

export interface UsePriorityReviewParams {
  preset: PriorityReviewPreset;
  page: number;
  pageSize: number;
  sortOrder: SortOrder;
}

export function usePriorityReview({
  preset,
  page,
  pageSize,
  sortOrder,
}: UsePriorityReviewParams) {
  const range = getDateRange(preset);

  const query: EventListQuery = {
    status: ["CREATED"],
    search: undefined,
    priority: [0],
    priorityMin: undefined,
    categoryId: undefined,
    page,
    pageSize,
    sortBy: "emailReceivedAt",
    sortOrder,
    ...range,
  };

  return useQuery({
    queryKey: ["priority-review", preset, page, pageSize, sortOrder],
    queryFn: () => fetchEvents(query),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}
